/**
 * Sourcing Preferences modal — mirrors the Wrangle "Preferences" drawer.
 *
 * Three tabs (left nav):
 *   General       — behaviour toggles (disable follow-ups, manual filters, classic shortlist)
 *   Visibility    — future controls placeholder
 *   Shared Context — org-wide sourcing context injected into every search
 *
 * Preferences are persisted to localStorage under `ggj.sourcing.prefs` so they
 * survive navigation and page refreshes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Eye, RotateCcw, Settings2, Share2 } from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface SourcingPrefs {
  disableFollowUps: boolean;
  showManualFiltersMode: boolean;
  useClassicShortlistMode: boolean;
  enableSourcingContext: boolean;
  sourcingContextText: string;
}

const LS_KEY = 'ggj.sourcing.prefs';

const DEFAULT_PREFS: SourcingPrefs = {
  disableFollowUps: false,
  showManualFiltersMode: false,
  useClassicShortlistMode: false,
  enableSourcingContext: false,
  sourcingContextText: '',
};

export function readSourcingPrefs(): SourcingPrefs {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<SourcingPrefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writeSourcingPrefs(prefs: SourcingPrefs): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type Tab = 'general' | 'visibility' | 'shared-context';

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }> }[] = [
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'visibility', label: 'Visibility', icon: Eye },
  { id: 'shared-context', label: 'Shared Context', icon: Share2 },
];

// ── Toggle component ──────────────────────────────────────────────────────────

function Toggle({ on, onChange }: { on: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none"
      style={{ background: on ? '#10B981' : 'var(--vscode-border-primary)' }}
    >
      <span
        className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200"
        style={{ transform: on ? 'translateX(17px)' : 'translateX(2px)' }}
      />
    </button>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
}

export function SourcingPreferencesModal({ onClose }: Props) {
  const [tab, setTab] = useState<Tab>('general');
  const [prefs, setPrefs] = useState<SourcingPrefs>(() => readSourcingPrefs());
  const overlayRef = useRef<HTMLDivElement>(null);

  // Persist on every change
  useEffect(() => {
    writeSourcingPrefs(prefs);
  }, [prefs]);

  // Dismiss on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Dismiss on backdrop click
  const onOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose],
  );

  const patch = useCallback(
    (partial: Partial<SourcingPrefs>) => setPrefs((p) => ({ ...p, ...partial })),
    [],
  );

  const reset = useCallback(() => {
    setPrefs(DEFAULT_PREFS);
  }, []);

  const tabTitle: Record<Tab, string> = {
    general: 'General Preferences',
    visibility: 'Visibility',
    'shared-context': 'Shared Context',
  };

  return (
    /* Backdrop */
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.35)' }}
      onClick={onOverlayClick}
    >
      {/* Modal card — fixed 520×420 so it never resizes between tabs */}
      <div
        className="relative flex w-[520px] overflow-hidden rounded-[14px] shadow-2xl"
        style={{
          background: 'var(--vscode-bg-elevated)',
          border: '1px solid var(--vscode-border-primary)',
          height: '420px',
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: 'calc(100dvh - 80px)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Sourcing preferences"
      >
        {/* ── Left nav ──────────────────────────────────────────────── */}
        <div
          className="flex w-[148px] flex-shrink-0 flex-col border-r py-4"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        >
          {/* Back to search */}
          <button
            type="button"
            onClick={onClose}
            className="mb-4 flex items-center gap-1.5 px-4 text-[12px] transition-colors hover:vscode-text-primary"
            style={{ color: 'var(--vscode-text-secondary)' }}
          >
            <ArrowLeft className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
            Back to search
          </button>

          <nav className="flex flex-col gap-0.5 px-2">
            {TABS.map(({ id, label, icon: Icon }) => {
              const active = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className="flex items-center gap-2 rounded-[8px] px-3 py-2 text-left text-[12.5px] font-medium transition-colors"
                  style={{
                    background: active ? 'var(--vscode-bg-tertiary)' : 'transparent',
                    color: active ? 'var(--vscode-text-primary)' : 'var(--vscode-text-secondary)',
                  }}
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                  {label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* ── Right content ─────────────────────────────────────────── */}
        <div
          className="flex min-w-0 flex-1 flex-col overflow-hidden"
          style={{ background: 'var(--vscode-bg-elevated)' }}
        >
          {/* Header */}
          <div
            className="flex flex-shrink-0 items-center justify-between border-b px-5 py-3.5"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <h2
              className="text-[13.5px] font-semibold"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {tabTitle[tab]}
            </h2>
            <button
              type="button"
              onClick={reset}
              className="flex items-center gap-1 text-[11.5px] font-medium transition-colors hover:vscode-text-primary"
              style={{ color: 'var(--vscode-text-tertiary)' }}
              title="Reset to defaults"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Reset
            </button>
          </div>

          {/* Body — scrollable inside fixed card height */}
          <div className="flex-1 overflow-y-auto px-5 py-4" style={{ minHeight: 0 }}>
            {tab === 'general' && (
              <div className="flex flex-col divide-y" style={{ borderColor: 'var(--vscode-border-primary)' }}>
                <ToggleRow
                  label="Disable follow-ups"
                  description="Remove the planning step from sourcing."
                  on={prefs.disableFollowUps}
                  onChange={(v) => patch({ disableFollowUps: v })}
                />
                <ToggleRow
                  label="Show manual filters mode"
                  description="Show a manual filters option in the search mode toggle."
                  on={prefs.showManualFiltersMode}
                  onChange={(v) => patch({ showManualFiltersMode: v })}
                />
                <ToggleRow
                  label="Use classic shortlist mode"
                  description="Show shortlist separately from collection (legacy behavior)."
                  on={prefs.useClassicShortlistMode}
                  onChange={(v) => patch({ useClassicShortlistMode: v })}
                />
              </div>
            )}

            {tab === 'visibility' && (
              <div className="flex flex-col gap-4">
                <ToggleRow
                  label="Show candidate scores"
                  description="Display AI-scored match percentages on candidate cards."
                  on={false}
                  onChange={() => {}}
                />
                <ToggleRow
                  label="Show pipeline stage badges"
                  description="Display current pipeline stage alongside candidate names."
                  on={false}
                  onChange={() => {}}
                />
              </div>
            )}

            {tab === 'shared-context' && (
              <div className="flex flex-col gap-4">
                <ToggleRow
                  label="Enable sourcing context"
                  description="Enable sourcing context for all searches."
                  on={prefs.enableSourcingContext}
                  onChange={(v) => patch({ enableSourcingContext: v })}
                />
                {prefs.enableSourcingContext && (
                  <div className="flex flex-col gap-1.5">
                    <textarea
                      value={prefs.sourcingContextText}
                      onChange={(e) => {
                        if (e.target.value.length <= 1000) {
                          patch({ sourcingContextText: e.target.value });
                        }
                      }}
                      rows={8}
                      placeholder="Describe your company, hiring priorities, or any context that should apply to every sourcing search…"
                      className="w-full resize-none rounded-[8px] border px-3 py-2.5 text-[12.5px] leading-relaxed outline-none focus:ring-1 focus:ring-[#10B981]"
                      style={{
                        background: 'var(--vscode-bg-elevated)',
                        borderColor: 'var(--vscode-border-primary)',
                        color: 'var(--vscode-text-primary)',
                      }}
                    />
                    <p
                      className="text-right text-[11px] tabular-nums"
                      style={{ color: 'var(--vscode-text-tertiary)' }}
                    >
                      {prefs.sourcingContextText.length}/1000
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ToggleRow helper ──────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  on,
  onChange,
}: {
  label: string;
  description: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5">
      <div className="min-w-0 flex-1">
        <p
          className="text-[12.5px] font-medium leading-snug"
          style={{ color: 'var(--vscode-text-primary)' }}
        >
          {label}
        </p>
        <p
          className="mt-0.5 text-[11.5px] leading-snug"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          {description}
        </p>
      </div>
      <div className="mt-0.5 flex-shrink-0">
        <Toggle on={on} onChange={onChange} />
      </div>
    </div>
  );
}
